import {
  createContext,
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
} from 'react';
import { lowerCase } from 'lodash';
// CMS
import { URLs } from '@/webiny/queries';
import { fetchWithFallback } from '@/webiny/api';
const { getToken } = URLs;
// Hooks
import { useAuth } from '@/hooks/useAuth';
import { useFeatureFlags } from './FeatureFlagProvider';
import { useWeb3 } from './Web3Provider';
// Utils
import { getCoinConversion } from '@/resources/wallet-service.resource';
import { INVALID_PRICE, isValidPrice, requireValidPrice, toCrypto } from '@/utils/coin';
import { findDefaultLayer1NativeCryptoAsset } from '@/providers/cryptoAssets';

const defaultCurrency = {
  key: 'ETH',
  name: 'Ethereum',
  symbol: 'ETH',
  allowPurchases: true,
  decimalPrecision: 18,
  displayPrecision: 6,
};

const CREDIT_CARD_SYMBOL = 'Credit / Debit Card';

export const CurrencyContext = createContext();

export function useCurrency() {
  return useContext(CurrencyContext);
}

export function CurrencyProvider({ children }) {
  const { featureFlags } = useFeatureFlags();
  const { fetcher } = useAuth();
  const { cryptoAssets } = useWeb3();
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [currencyKey, setCurrencyKey] = useState(defaultCurrency.key);
  const [currencyConversion, setCurrencyConversion] = useState(INVALID_PRICE);
  const [conversionError, setConversionError] = useState(null);
  const [isSwitchPay, setIsSwitchPay] = useState(false);
  // transferPrecision is a map of currency symbols to their decimal precision for all coins, not just accepted erc20
  const [transferPrecision, setTransferPrecision] = useState({});

  const additionalCurrencies = useMemo(
    () => [
      {
        name: 'Credit / Debit Card',
        key: CREDIT_CARD_SYMBOL,
        symbol: CREDIT_CARD_SYMBOL,
        allowPurchases: featureFlags?.ShowStripe || featureFlags?.EnableInvoiceSwitchPay,
        decimalPrecision: 2,
        displayPrecision: 2,
      },
    ],
    [featureFlags.ShowStripe, featureFlags.EnableInvoiceSwitchPay]
  );

  const selectedCurrency = useMemo(
    () =>
      allCurrencies.find((asset) => asset.key === currencyKey) ||
      allCurrencies.find((asset) => asset.symbol === currencyKey) ||
      defaultCurrency,
    [allCurrencies, currencyKey]
  );
  const currency = selectedCurrency.symbol;
  const selectedPaymentAsset = selectedCurrency.cryptoAsset || null;
  const selectedPaymentFeeAsset = selectedPaymentAsset?.fee.cryptoAssetUniqueKey
    ? cryptoAssets.find(
        (asset) => asset.uniqueKey === selectedPaymentAsset.fee.cryptoAssetUniqueKey
      ) || null
    : null;
  const selectedPaymentFeeSymbol = selectedPaymentFeeAsset?.display.symbol;
  const selectedPaymentFeeDisplayPrecision = selectedPaymentFeeAsset
    ? Math.min(selectedPaymentFeeAsset.display.decimalPrecision, 6)
    : selectedCurrency.displayPrecision;
  const invoicePaymentSymbol = selectedPaymentAsset?.asset.canonicalName ?? currency;
  const isCreditCardPayment = selectedCurrency.symbol === CREDIT_CARD_SYMBOL;

  const getCurrencyConversion = useCallback(
    ({ currency }) => {
      const invalidPriceError = {
        header: 'Currency conversion error',
        message: `${currency} conversion is unavailable at this time.`,
      };

      if (!isCreditCardPayment) {
        return fetcher(getCoinConversion({ symbol: currency, currency: 'USD' }))
          .then((rate) => {
            const price = requireValidPrice(rate.price, INVALID_PRICE);
            setConversionError(isValidPrice(price) ? null : invalidPriceError);
            setCurrencyConversion(price);
            return price;
          })
          .catch((err) => {
            console.error(err);
            setConversionError(invalidPriceError);
            setCurrencyConversion(INVALID_PRICE);
            return INVALID_PRICE;
          });
      }
    },
    [fetcher, isCreditCardPayment]
  );

  // fetch accepted currencies on mount
  useEffect(() => {
    async function fetchCurrencies() {
      try {
        const purchaseAssets = cryptoAssets
          .filter(
            (coin) =>
              coin.asset.settings?.allowPurchases &&
              coin.traits.isDefaultLayer1 &&
              coin.traits.isEvm &&
              (coin.traits.isNative || coin.traits.isErc20)
          )
          .map((coin) => ({
            allowPurchases: coin.asset.settings.allowPurchases,
            key: coin.uniqueKey,
            symbol: coin.asset.canonicalName,
            name: coin.display.name,
            contractAddress: coin.asset.definitionAddress,
            decimalPrecision: coin.display.decimalPrecision,
            displayPrecision: Math.min(coin.display.decimalPrecision, 6),
            cryptoAsset: coin,
          }));

        const defaultLayer1NativeAsset = findDefaultLayer1NativeCryptoAsset(cryptoAssets);
        const defaultPurchaseAsset = purchaseAssets.find(
          (asset) => asset.key === defaultLayer1NativeAsset?.uniqueKey
        );
        const currencies = [
          defaultPurchaseAsset || defaultCurrency,
          ...additionalCurrencies,
          ...purchaseAssets.filter((asset) => asset.key !== defaultPurchaseAsset?.key),
        ];

        const currencyImageUrls = await Promise.allSettled(
          currencies.map((currency) => {
            const slug = lowerCase(currency.symbol);
            return fetchWithFallback(getToken(slug), {});
          })
        );

        const urls = currencyImageUrls.reduce((acc, currency, index) => {
          currency.status === 'fulfilled' && currency.value.icon
            ? (acc[currencies[index].symbol] = currency.value.icon)
            : (acc[currencies[index].symbol] = ''); // if we don't find an icon, we'll just use an empty string to have the browser show a broken image icon
          return acc;
        }, {});

        const all = currencies.map((currency) => ({
          ...currency,
          icon: urls[currency.symbol],
        }));
        setAllCurrencies(all);

        if (defaultPurchaseAsset) {
          setCurrencyKey((previousKey) =>
            previousKey === defaultCurrency.key ? defaultPurchaseAsset.key : previousKey
          );
        }

        const precisionMap = all.reduce((acc, curr) => {
          acc[curr.symbol] = curr.decimalPrecision;
          return acc;
        }, {});
        setTransferPrecision(precisionMap);
      } catch (err) {
        console.error(err);
        const all = [defaultCurrency, ...additionalCurrencies];
        setAllCurrencies(all);
        const precisionMap = all.reduce((acc, curr) => {
          acc[curr.symbol] = curr.decimalPrecision;
          return acc;
        }, {});
        setTransferPrecision(precisionMap);
      }
    }
    cryptoAssets.length && fetchCurrencies();
  }, [
    additionalCurrencies,
    featureFlags?.ShowStripe,
    featureFlags?.EnableInvoiceSwitchPay,
    cryptoAssets,
  ]);

  // fetch conversion rate on mount and when currency changes
  useEffect(() => {
    getCurrencyConversion({ currency });
  }, [currency]);

  const handleSetCurrency = (value) => {
    const selectedCurrency =
      allCurrencies.find((asset) => asset.key === value) ||
      allCurrencies.find((asset) => asset.symbol === value) ||
      defaultCurrency;

    setCurrencyKey(selectedCurrency.key);
  };

  const handleConvertUsdToCoin = (amount) => {
    if (!amount || !isValidPrice(currencyConversion)) return 0;
    const precision =
      selectedCurrency?.decimalPrecision ?? amount?.toString().split('.')[1]?.length;
    return toCrypto({ amount, precision, conversion: currencyConversion });
  };

  return (
    <CurrencyContext.Provider
      value={{
        allCurrencies,
        conversionError,
        currency,
        currencyKey,
        currencyConversion,
        handleConvertUsdToCoin,
        handleSetCurrency,
        invoicePaymentSymbol,
        isCreditCardPayment,
        isSwitchPay,
        selectedCurrency,
        selectedPaymentFeeAsset,
        selectedPaymentFeeDisplayPrecision,
        selectedPaymentFeeSymbol,
        selectedPaymentAsset,
        setCurrency: handleSetCurrency,
        setIsSwitchPay,
        transferPrecision,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}
